import type { Weekday } from "./scheduleTypes";

export type MonthlyWeekOrdinal =
  | "first"
  | "second"
  | "third"
  | "fourth"
  | "last";

export type MonthlyRepeatRule =
  | { kind: "day_of_month"; day: number }
  | { kind: "ordinal_weekday"; ordinal: MonthlyWeekOrdinal; weekday: Weekday }
  | { kind: "last_day" };

export interface MonthlyRepeatOption {
  label: string;
  value: MonthlyRepeatRule;
}

const WEEKDAY_NAMES: Record<Weekday, string> = {
  sun: "Sunday",
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
};

const WEEKDAY_BY_INDEX: readonly Weekday[] = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
];

const ORDINAL_BY_OCCURRENCE: readonly MonthlyWeekOrdinal[] = [
  "first",
  "second",
  "third",
  "fourth",
];

const parseIsoDate = (isoDate: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

/**
 * Produces the two Google Calendar-style monthly rules valid for a selected
 * start date. Calendar-end rules stay semantic so a scheduler can safely
 * handle February, leap years, and shorter months.
 */
export const getMonthlyRepeatOptions = (
  startsOn: string,
): MonthlyRepeatOption[] => {
  const date = parseIsoDate(startsOn);
  if (!date) return [];

  const day = date.getDate();
  const weekday = WEEKDAY_BY_INDEX[date.getDay()];
  // Dates 28–31 must be represented as a month-end rule. A numeric monthly
  // date would skip February and some 30-day months; the semantic rule runs
  // on the actual final calendar day instead, including leap-year February.
  const isMonthEndRule = day >= 28;
  const monthLastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const isLastWeekday = isMonthEndRule || day + 7 > monthLastDay;
  const occurrence = Math.floor((day - 1) / 7);
  const ordinal = isLastWeekday
    ? "last"
    : ORDINAL_BY_OCCURRENCE[occurrence];

  return [
    isMonthEndRule
      ? {
          label: "Monthly on the last day",
          value: { kind: "last_day" },
        }
      : {
          label: `Monthly on day ${day}`,
          value: { kind: "day_of_month", day },
        },
    {
      label: `Monthly on the ${ordinal} ${WEEKDAY_NAMES[weekday]}`,
      value: { kind: "ordinal_weekday", ordinal, weekday },
    },
  ];
};
